import React, { useState } from "react";
import { publishNewsletter } from "../../../../services/newsletterServices";
import { toOffsetISOString } from "../../../../utils/newsletterDateUtils";
import { getSubscriberRoleMeta } from "../subscriberRoleUtils";
import ContentSectionCard from "../../content/ContentSectionCard";
import NewsletterPublishSummaryDialog from "../NewsletterPublishSummaryDialog";
import NewsletterPublishTemplateSection from "./NewsletterPublishTemplateSection";
import NewsletterScheduleSection, { SCHEDULE_LATER } from "./NewsletterScheduleSection";
import NewsletterPublishFormLayout from "./NewsletterPublishFormLayout";
import NewsletterSubscriberSelect from "./NewsletterSubscriberSelect";
import { useNewsletterPublishState } from "./useNewsletterPublishState";
import { useNewsletterScheduleState } from "./useNewsletterScheduleState";
import { buildPublishSuccessMessage } from "./newsletterPublishMessages";
import { useNewsletterPublishFeedback } from "./useNewsletterPublishFeedback";

const NewsletterPublishSelectedView = () => {
  const notify = useNewsletterPublishFeedback();
  const publishState = useNewsletterPublishState();
  const {
    templatesLoading,
    templateLoading,
    selectedTemplateKey,
    setSelectedTemplateKey,
    subject,
    handleSubjectChange,
    compileErrors,
    subjectWarning,
    templateOptions,
    canPrepare,
    compiledHtml,
  } = publishState;

  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const {
    scheduleMode,
    scheduledAt,
    scheduledAtError,
    handleScheduleModeChange,
    handleScheduledAtChange,
    validateSchedule,
  } = useNewsletterScheduleState();
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const handlePrepareSend = () => {
    if (!canPrepare) {
      notify({
        title: "Invio non disponibile",
        message: "Seleziona un template valido con oggetto e HTML compilato.",
      });
      return;
    }
    if (!validateSchedule()) {
      return;
    }
    if (!selectedSubscribers.length) {
      notify({ title: "Nessun destinatario", message: "Seleziona almeno un iscritto." });
      return;
    }
    setSummaryOpen(true);
  };

  const handleConfirmSend = async (emails) => {
    setSending(true);
    try {
      const result = await publishNewsletter({
        emails,
        subject: subject.trim(),
        htmlBody: compiledHtml,
        scheduledAt: scheduleMode === SCHEDULE_LATER ? toOffsetISOString(scheduledAt) : undefined,
      });
      setSummaryOpen(false);
      const msg = buildPublishSuccessMessage(result, scheduleMode);
      notify({ ...msg, detail: result.historyId ? `ID storico: ${result.historyId}` : undefined });
    } catch (err) {
      notify({ title: "Invio non riuscito", message: err.message });
    } finally {
      setSending(false);
    }
  };

  const summaryRecipients = selectedSubscribers.map((item) => ({
    email: item.email,
    role: item.role,
    roleLabel: getSubscriberRoleMeta(item.role).label,
  }));

  return (
    <>
      <NewsletterPublishFormLayout
        onConfirm={handlePrepareSend}
        confirmDisabled={!canPrepare || !selectedSubscribers.length}
      >
        <ContentSectionCard title="Template e oggetto">
          <NewsletterPublishTemplateSection
            templatesLoading={templatesLoading}
            templateLoading={templateLoading}
            selectedTemplateKey={selectedTemplateKey}
            onTemplateChange={setSelectedTemplateKey}
            templateOptions={templateOptions}
            subjectWarning={subjectWarning}
            compileErrors={compileErrors}
            subject={subject}
            onSubjectChange={handleSubjectChange}
          />
        </ContentSectionCard>
        <ContentSectionCard title="Destinatari selezionati">
          <NewsletterSubscriberSelect
            value={selectedSubscribers}
            onChange={setSelectedSubscribers}
            onError={(message) => notify({ title: "Errore ricerca iscritti", message })}
          />
        </ContentSectionCard>
        <ContentSectionCard title="Programmazione">
          <NewsletterScheduleSection
            scheduleMode={scheduleMode}
            onScheduleModeChange={handleScheduleModeChange}
            scheduledAt={scheduledAt}
            onScheduledAtChange={handleScheduledAtChange}
            scheduledAtError={scheduledAtError}
          />
        </ContentSectionCard>
      </NewsletterPublishFormLayout>
      <NewsletterPublishSummaryDialog
        open={summaryOpen}
        recipients={summaryRecipients}
        subject={subject}
        htmlPreview={compiledHtml}
        onClose={() => !sending && setSummaryOpen(false)}
        onConfirm={handleConfirmSend}
        sending={sending}
        scheduledAt={scheduleMode === SCHEDULE_LATER ? toOffsetISOString(scheduledAt) : undefined}
      />
    </>
  );
};

export default NewsletterPublishSelectedView;
